/**
 * Client-side uplink mic health for post-call RCA (silent/wrong device/muted track).
 * Durable HTTP events only — no high-rate spam.
 */
import {
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type TrackPublication,
} from 'livekit-client';
import { reportClientRca } from './reportClientRca';

/** Approx dBFS from Analyser byte time-domain (0 = full scale). */
export function micRmsDbFromTimeDomain(data: Uint8Array): { rmsDb: number; peakDb: number } {
  if (!data.length) return { rmsDb: -120, peakDb: -120 };
  let sumSq = 0;
  let peak = 0;
  for (let i = 0; i < data.length; i += 1) {
    const v = (data[i] - 128) / 128;
    sumSq += v * v;
    const a = Math.abs(v);
    if (a > peak) peak = a;
  }
  const rms = Math.sqrt(sumSq / data.length);
  const rmsDb = rms > 1e-8 ? Math.round(20 * Math.log10(rms)) : -120;
  const peakDb = peak > 1e-8 ? Math.round(20 * Math.log10(peak)) : -120;
  return { rmsDb, peakDb };
}

type MicLevelSampler = {
  sample: () => { rmsDb: number; peakDb: number } | null;
  dispose: () => void;
};

function createMicLevelSampler(track: MediaStreamTrack | null | undefined): MicLevelSampler | null {
  if (!track || track.readyState === 'ended') return null;
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return null;
    const ctx = new Ctx();
    const stream = new MediaStream([track]);
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    return {
      sample: () => {
        if (track.readyState === 'ended') return null;
        analyser.getByteTimeDomainData(buf);
        return micRmsDbFromTimeDomain(buf);
      },
      dispose: () => {
        try {
          source.disconnect();
        } catch {
          /* */
        }
        void ctx.close().catch(() => undefined);
      },
    };
  } catch {
    return null;
  }
}

function localMicPublication(room: Room): LocalTrackPublication | undefined {
  const direct = room.localParticipant.getTrackPublication(Track.Source.Microphone);
  if (direct) return direct as LocalTrackPublication;
  let found: LocalTrackPublication | undefined;
  room.localParticipant.audioTrackPublications.forEach((pub) => {
    if (pub.source === Track.Source.Microphone || pub.kind === Track.Kind.Audio) {
      found = pub as LocalTrackPublication;
    }
  });
  return found;
}

function mediaTrackFromPublication(pub: TrackPublication | undefined): MediaStreamTrack | null {
  if (!pub?.track) return null;
  const t = pub.track.mediaStreamTrack;
  return t || null;
}

function trackConstraintFlags(settings: MediaTrackSettings): Record<string, number> {
  return {
    echoCancellation: settings.echoCancellation === true ? 1 : settings.echoCancellation === false ? 0 : -1,
    noiseSuppression: settings.noiseSuppression === true ? 1 : settings.noiseSuppression === false ? 0 : -1,
    autoGainControl: settings.autoGainControl === true ? 1 : settings.autoGainControl === false ? 0 : -1,
    sampleRate: typeof settings.sampleRate === 'number' ? settings.sampleRate : -1,
    channelCount: typeof settings.channelCount === 'number' ? settings.channelCount : -1,
  };
}

async function countAudioInputs(): Promise<number> {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return -1;
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === 'audioinput').length;
  } catch {
    return -1;
  }
}

export async function collectLocalMicMeta(
  room: Room,
  sampler: MicLevelSampler | null,
  phase: string,
  extra?: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const pub = localMicPublication(room);
  const mediaTrack = mediaTrackFromPublication(pub);
  const level = sampler?.sample() ?? null;
  const settings = mediaTrack?.getSettings?.() ?? {};
  const flags = trackConstraintFlags(settings as MediaTrackSettings);
  const audioInputCount = await countAudioInputs();
  return {
    phase,
    track_sid: pub?.trackSid,
    trackEnabled: mediaTrack ? (mediaTrack.enabled ? 1 : 0) : -1,
    trackMuted: mediaTrack ? (mediaTrack.muted ? 1 : 0) : -1,
    pubMuted: pub ? (pub.isMuted ? 1 : 0) : -1,
    trackReadyState: mediaTrack ? String(mediaTrack.readyState) : 'missing',
    mic_rms_db: level?.rmsDb ?? -120,
    mic_peak_db: level?.peakDb ?? -120,
    audioInputCount,
    ...flags,
    ...(extra || {}),
  };
}

/** Haircheck / pre-room preview — no LiveKit publication yet. */
export async function reportLocalMicHaircheck(
  sessionId: string,
  mediaStream: MediaStream | null | undefined,
): Promise<void> {
  if (!sessionId) return;
  const track = mediaStream?.getAudioTracks?.()[0];
  const sampler = createMicLevelSampler(track ?? null);
  const level = sampler?.sample() ?? null;
  const settings = track?.getSettings?.() ?? {};
  const flags = trackConstraintFlags(settings as MediaTrackSettings);
  const audioInputCount = await countAudioInputs();
  reportClientRca(sessionId, 'local_mic_haircheck', {
    phase: 'haircheck',
    trackEnabled: track ? (track.enabled ? 1 : 0) : 0,
    trackMuted: track ? (track.muted ? 1 : 0) : 0,
    trackReadyState: track ? String(track.readyState) : 'missing',
    mic_rms_db: level?.rmsDb ?? -120,
    mic_peak_db: level?.peakDb ?? -120,
    audioInputCount,
    ...flags,
  });
  sampler?.dispose();
}

const SILENT_RMS_DB = -52;
const SILENT_STREAK_NEED = 4;
const SAMPLE_INTERVAL_MS = 5000;

/**
 * Periodic mic level + publish state; flags likely silent uplink while RTP still flows.
 */
export function installLocalMicDiagnostics(room: Room, sessionId: string): () => void {
  let sampler: MicLevelSampler | null = null;
  let samplerTrack: MediaStreamTrack | null = null;
  const intervalIds: number[] = [];
  const timeoutIds: number[] = [];
  const startedAt = Date.now();
  let silentStreak = 0;
  let silentSuspectReported = 0;
  let baselineBytes = 0;
  let lastBytes = 0;

  const refreshSampler = () => {
    const track = mediaTrackFromPublication(localMicPublication(room));
    if (!track || track === samplerTrack) return;
    sampler?.dispose();
    samplerTrack = track;
    sampler = createMicLevelSampler(track);
  };

  const report = async (phase: string, extra?: Record<string, unknown>) => {
    refreshSampler();
    const callSec = Math.round((Date.now() - startedAt) / 1000);
    const meta = await collectLocalMicMeta(room, sampler, phase, { callSec, ...(extra || {}) });
    reportClientRca(sessionId, 'local_mic_snapshot', meta);
  };

  const onPublished = (pub: TrackPublication) => {
    if (pub.kind !== Track.Kind.Audio && pub.source !== Track.Source.Microphone) return;
    refreshSampler();
    void report('track_published', { track_sid: pub.trackSid });
  };
  room.on(RoomEvent.LocalTrackPublished, onPublished as (...args: unknown[]) => void);

  timeoutIds.push(window.setTimeout(() => void report('join_3s'), 3000));
  timeoutIds.push(window.setTimeout(() => void report('join_15s'), 15000));

  let tick = 0;
  intervalIds.push(
    window.setInterval(() => {
      tick += 1;
      refreshSampler();
      const level = sampler?.sample();
      const rms = level?.rmsDb ?? -120;
      const pub = localMicPublication(room);
      const mediaTrack = mediaTrackFromPublication(pub);
      const userMuted =
        (mediaTrack && !mediaTrack.enabled) ||
        (mediaTrack && mediaTrack.muted) ||
        (pub && pub.isMuted);

      if (userMuted) {
        silentStreak = 0;
        return;
      }

      if (rms < SILENT_RMS_DB) {
        silentStreak += 1;
      } else {
        silentStreak = 0;
      }

      // ~30s durable samples (every 6th tick at 5s)
      if (tick % 6 === 0) {
        void report('periodic');
      }

      if (silentStreak >= SILENT_STREAK_NEED && silentSuspectReported === 0) {
        const bytesDelta = lastBytes - baselineBytes;
        if (lastBytes > baselineBytes && bytesDelta > 8000) {
          silentSuspectReported = 1;
          void report('silent_suspect', { silentSuspect: 1, audio_bytes_sent: lastBytes });
          reportClientRca(sessionId, 'local_mic_silent_suspect', {
            silentSuspect: 1,
            mic_rms_db: rms,
            audio_bytes_sent: lastBytes,
            callSec: Math.round((Date.now() - startedAt) / 1000),
          });
        }
      }
    }, SAMPLE_INTERVAL_MS),
  );

  // Correlate with publisher bytes (import lazily to avoid circular deps)
  intervalIds.push(
    window.setInterval(() => {
      void import('./clientRca')
        .then(({ collectPublisherOutboundAudio }) => collectPublisherOutboundAudio(room))
        .then(({ audio_bytes_sent }) => {
          if (baselineBytes === 0 && audio_bytes_sent > 0) baselineBytes = audio_bytes_sent;
          lastBytes = audio_bytes_sent;
        })
        .catch(() => undefined);
    }, SAMPLE_INTERVAL_MS),
  );

  return () => {
    room.off(RoomEvent.LocalTrackPublished, onPublished as (...args: unknown[]) => void);
    intervalIds.forEach((id) => window.clearInterval(id));
    timeoutIds.forEach((id) => window.clearTimeout(id));
    void report('end');
    sampler?.dispose();
    sampler = null;
    samplerTrack = null;
  };
}
